/**
 * Hover interaction system.
 *
 * Uses raycasting against node bounding spheres to detect proximity.
 * When the cursor approaches a node:
 *   1. Node emits a soft halo (expand over 200ms)
 *   2. Connected nodes increase intensity
 *   3. Edges to connected nodes become visible
 *   4. After 800ms linger, context layer fades in
 *
 * No tooltips. No popups. Context through spatial emphasis.
 */

import * as THREE from 'three';
import { NodeRenderer } from '../scene/nodes';
import { EdgeRenderer } from '../scene/edges';
import { useAuraStore } from '../state/store';
import type { SyntheticField } from '../data/synthetic';

const HOVER_THRESHOLD = 15; // Screen-space pixels
const LINGER_DELAY = 800; // ms before showing context
const FLAG_HOVERED = 2; // Bit flag for hovered state

export class HoverSystem {
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2(-10000, -10000); // Off-screen initially
  private lastHoveredIndex = -1;
  private hoverStartTime = 0;
  private lingerTriggered = false;

  private boundPositions: THREE.Vector3[] = [];
  private contextPanel: HTMLElement | null;
  private contextName: HTMLElement | null;
  private contextDetail: HTMLElement | null;

  constructor(
    private container: HTMLElement,
    private camera: THREE.PerspectiveCamera,
    private nodeRenderer: NodeRenderer,
    private edgeRenderer: EdgeRenderer,
    private field: SyntheticField,
  ) {
    this.contextPanel = document.getElementById('context-panel');
    this.contextName = document.getElementById('context-name');
    this.contextDetail = document.getElementById('context-detail');

    // Pre-compute world positions
    this.boundPositions = field.nodes.map((n) =>
      new THREE.Vector3(...n.position),
    );

    // Track mouse position
    container.addEventListener('pointermove', (e) => {
      const rect = container.getBoundingClientRect();
      this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    });
  }

  /**
   * Called every frame.
   */
  update() {
    const store = useAuraStore.getState();
    if (!store.entryCompleted) return;

    // Find nearest node to cursor via raycasting
    this.raycaster.setFromCamera(this.mouse, this.camera);

    let closestIndex = -1;
    let closestDist = Infinity;

    this.boundPositions.forEach((pos, i) => {
      // Project to screen space and check distance to ray
      const projected = pos.clone().project(this.camera);
      const screenPos = new THREE.Vector2(projected.x, projected.y);
      const mouseDist = screenPos.distanceTo(this.mouse);

      // Scale threshold by depth (farther nodes need less precision)
      const depth = pos.distanceTo(this.camera.position);
      const adjustedThreshold = HOVER_THRESHOLD / 1000 * (depth / 200);

      if (mouseDist < adjustedThreshold && depth < closestDist) {
        closestDist = depth;
        closestIndex = i;
      }
    });

    // State change detection
    if (closestIndex !== this.lastHoveredIndex) {
      // Clear previous hover
      if (this.lastHoveredIndex >= 0) {
        this.nodeRenderer.setNodeFlag(this.lastHoveredIndex, 0);
        this.edgeRenderer.hideAll();
        this.hideContext();
      }

      // Apply new hover
      if (closestIndex >= 0) {
        this.nodeRenderer.setNodeFlag(closestIndex, FLAG_HOVERED);
        this.edgeRenderer.showEdgesForNode(closestIndex, this.field.edges);
        this.hoverStartTime = performance.now();
        this.lingerTriggered = false;
      }

      this.lastHoveredIndex = closestIndex;
      store.setHoveredNode(closestIndex);
    }

    // Linger detection — show context after 800ms
    if (
      closestIndex >= 0 &&
      !this.lingerTriggered &&
      performance.now() - this.hoverStartTime > LINGER_DELAY
    ) {
      this.lingerTriggered = true;
      this.showContext(closestIndex);
    }
  }

  private showContext(nodeIndex: number) {
    const node = this.field.nodes[nodeIndex];
    if (!node || !this.contextPanel || !this.contextName || !this.contextDetail) return;

    this.contextName.textContent = node.name;
    this.contextDetail.textContent = `${node.category} · ${node.trend} · ${(node.intensity * 100).toFixed(0)}% signal`;
    this.contextPanel.classList.add('visible');
  }

  private hideContext() {
    if (this.contextPanel) this.contextPanel.classList.remove('visible');
  }

  dispose() {
    // Mouse listener on container is cleaned up with container removal
  }
}
